import { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    ContextHolder, 
    useAuth as useFronteggAuth,
    useLoginWithRedirect,
} from '@frontegg/react';

import getUserDataOnLogin from '../hooks/user/getUserDataOnLogin';
import CustomSnackbar from '../components/elements/CustomSnackbar';
import { SESSION_KEY_CLIENT_ID, SESSION_KEY_JWT, SESSION_KEY_LOCATION_ID } from '../constants';

import { useUser } from './UserContext';
import { useCart } from './CartContext';

interface AuthContextType {
    isAuthenticated: boolean;
    handleLoginClick: () => void;
    loginUser: ( userToken: string ) => Promise<boolean>;
    logoutUser: () => void;
    sessionExpired: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: React.ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const navigate = useNavigate();
    const { resetUserContext, updateUserAndSession } = useUser();
    const { resetCartContext, fetchCart, applyPaymentMethod } = useCart();
    const { user, isAuthenticated: isFronteggAuthenticated } = useFronteggAuth();
    const loginWithRedirect = useLoginWithRedirect();

    const [ isAuthenticated, setIsAuthenticated ] = useState(false);
    const [ snackbar, setSnackbar ] = useState({ open: false, message: '', severity: 'info' });


    useEffect(() => {
        const jwtToken = localStorage.getItem(SESSION_KEY_JWT);
        setIsAuthenticated(!!jwtToken);

        const interceptor = axios.interceptors.response.use(
            response => response,
            error => {
                if (error.response && error.response.status === 401) {
                    const requestUrl = error.response.config.url;
                    if (requestUrl && requestUrl.includes('cart')) {
                        sessionExpired();
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, [navigate]);

	useEffect(() => {
        if (isFronteggAuthenticated) {
            if (user?.accessToken) {
                localStorage.setItem(SESSION_KEY_JWT, user.accessToken);
                loginUser(user.accessToken);
            } else {
                clearSession();
                setSnackbar({ open: true, message: 'An error occurred while logging in.', severity: 'error' });
            }
        }
    }, [ isFronteggAuthenticated, loginWithRedirect ]);

    const handleLoginClick = () => {
        if (isFronteggAuthenticated) {
            logoutUser();
        } else {
            window.localStorage.setItem('FRONTEGG_AFTER_AUTH_REDIRECT_URL', window.location.href);
            loginWithRedirect();
        }
    };

    const loginUser = async ( accessToken: string ) => {
        setIsAuthenticated(true);
        const { success, message } = await getUserDataOnLogin(accessToken, updateUserAndSession, fetchCart, applyPaymentMethod);
        if (!success) {
            setSnackbar({ open: true, message: `An error occurred while logging in: ${message}`, severity: 'error' });
            clearSession();
        }
        return success;
    };

    const sessionExpired = () => {
        clearSession();
        setSnackbar({ open: true, message: 'Session has expired.', severity: 'error' });
    };

    const logoutUser = () => {
        clearSession();
        setSnackbar({ open: true, message: 'Successfully logged out', severity: 'info' });

        const baseUrl = ContextHolder.getContext().baseUrl;
        window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location.href}`;
    };

    const clearSession = () => {
        const clientIdFromStorage = localStorage.getItem(SESSION_KEY_CLIENT_ID) as string;
        const locationIdFromStorage = localStorage.getItem(SESSION_KEY_LOCATION_ID) as string;
        resetUserContext();
        resetCartContext();
        localStorage.clear();
        localStorage.setItem(SESSION_KEY_CLIENT_ID, clientIdFromStorage);
        localStorage.setItem(SESSION_KEY_LOCATION_ID, locationIdFromStorage);
        setIsAuthenticated(false);
        navigate('/');
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const contextValue = {
        isAuthenticated,
        handleLoginClick,
        loginUser,
        logoutUser,
        sessionExpired
    };

    return <AuthContext.Provider value={contextValue}>{children}
        <CustomSnackbar
            open={snackbar.open}
            message={snackbar.message}
            severity={snackbar.severity}
            handleClose={handleCloseSnackbar}
        />
    </AuthContext.Provider>;
};

export { AuthProvider, useAuth };
